import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { EmptySectionMessage } from '@tassomutuo-backoffice/ui';

import { MIN_SEARCH_LENGTH } from '../../../../hooks/useUrlListParams';

import {
  listManagedDropRequests,
  ManagedDropReviewListParams,
} from '../../../../services/api/managed-leads/managedDropReview';
import {
  getManagedLeadLifecycle,
  ManagedLeadProductPath,
} from '../../../../services/api/managed-leads/managedLeads';
import ManagedLeadFailureAlert from '../../../managedLead/components/ManagedLeadFailureAlert';
import TassoQuintoErrorAlert from '../../../managedLead/components/TassoQuintoErrorAlert';
import {
  MANAGED_LEAD_QUERY_OPTIONS,
  managedDropReviewQueryKey,
  managedLeadLifecycleQueryKey,
} from '../../../managedLead/managedLeadQueryKeys';
import { ManagedDropReviewOutcome } from '../managedDropReviewFeedback';
import useManagedDropReviewParams from '../hooks/useManagedDropReviewParams';
import {
  formatManagedDropReviewStatus,
  MANAGED_DROP_REVIEW_STATUSES,
} from '../managedDropReviewFormatting';
import ManagedDropReviewCard from './ManagedDropReviewCard';

const PAGE_SIZE = 25 as const;

interface ManagedDropReviewPanelProps {
  product: ManagedLeadProductPath;
  productName: string;
  searchHelpText: string;
  /** Whether this user holds the review scope the whole queue sits behind. */
  canReview: boolean;
}

/**
 * The administrative Drop queue of one product: pending requests and history
 * together, filtered by state, searched over the fields the product declares
 * searchable, and paginated 25 by the server. Approva and Rifiuta live on the
 * cards, because only a pending request has them.
 */
const ManagedDropReviewPanel = ({
  product,
  productName,
  searchHelpText,
  canReview,
}: ManagedDropReviewPanelProps) => {
  const params = useManagedDropReviewParams();
  const listParams: ManagedDropReviewListParams = {
    page: params.page,
    limit: PAGE_SIZE,
    ...(params.search.length >= MIN_SEARCH_LENGTH
      ? { search: params.search }
      : {}),
    ...(params.statuses.length ? { status: params.statuses.join(',') } : {}),
  };
  const query = useQuery({
    ...MANAGED_LEAD_QUERY_OPTIONS,
    queryKey: [...managedDropReviewQueryKey(product), listParams],
    queryFn: () => listManagedDropRequests(product, listParams),
    // Reading the queue crosses owners, so it is the review scope's business
    // too: without it there is nothing to ask for.
    enabled: canReview,
  });
  // Only to name the stage a request was made from: the current stage travels
  // with the queue item, the stage at request is an id.
  const lifecycleQuery = useQuery({
    ...MANAGED_LEAD_QUERY_OPTIONS,
    queryKey: managedLeadLifecycleQueryKey(product),
    queryFn: () => getManagedLeadLifecycle(product),
    enabled: canReview,
  });
  /**
   * What the last decision did, kept here and not on the card that made it: an
   * approval or a closed request leaves a filtered queue as soon as it is
   * re-read, and the card would take the only account of it away with itself.
   */
  const [outcome, setOutcome] = useState<ManagedDropReviewOutcome | null>(null);
  const listParamsKey = JSON.stringify(listParams);
  useEffect(() => {
    // Another page or another filter is another set of requests: an outcome
    // about one of them no longer describes what is on screen.
    setOutcome(null);
  }, [listParamsKey]);
  const stageLabelsById = new Map(
    (lifecycleQuery.data?.stages ?? []).map((stage) => [stage.id, stage.label])
  );

  const totalLabel =
    query.data?.totalResults === null
      ? 'Totale richieste non disponibile'
      : query.data?.totalResults === 1
      ? '1 richiesta'
      : `${query.data?.totalResults ?? 0} richieste`;

  if (!canReview) {
    return (
      <Alert severity="error">
        {`Non sei autorizzato a revisionare le richieste di Drop ${productName}.`}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack spacing={2} mb={3}>
        <TextField
          type="search"
          label={`Cerca richieste ${productName}`}
          value={params.searchInput}
          helperText={searchHelpText}
          onChange={(event) => params.setSearchInput(event.target.value)}
        />

        <FormControl component="fieldset" variant="standard">
          <FormLabel component="legend">Filtra per stato</FormLabel>
          <FormGroup row>
            {MANAGED_DROP_REVIEW_STATUSES.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={params.statuses.includes(status)}
                    onChange={() => params.toggleStatus(status)}
                  />
                }
                label={formatManagedDropReviewStatus(status)}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Stack>

      {outcome &&
        (outcome.severity === 'success' ? (
          <Alert severity="success" role="status" sx={{ mb: 2 }}>
            {outcome.message}
          </Alert>
        ) : (
          <Box mb={2}>
            <ManagedLeadFailureAlert
              message={outcome.message}
              severity={outcome.severity}
              retryable={outcome.retryable}
              sessionExpired={outcome.sessionExpired}
              onRetry={outcome.retry}
            />
          </Box>
        ))}

      {query.isPending && (
        <Typography role="status">Caricamento richieste di Drop…</Typography>
      )}

      {query.isError && (
        <TassoQuintoErrorAlert
          error={query.error}
          retry={() => query.refetch()}
          retryableMessage="Non è stato possibile caricare le richieste di Drop. Riprova."
          unauthorizedMessage={`Non sei autorizzato a revisionare le richieste di Drop ${productName}.`}
          notFoundMessage="Le richieste di Drop di questo prodotto non sono disponibili."
        />
      )}

      {query.data?.results.length === 0 && (
        <EmptySectionMessage title="Non ci sono richieste di Drop da mostrare" />
      )}

      {query.data && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            mb={2}
          >
            <Typography>{totalLabel}</Typography>
            <Typography color="text.secondary" variant="body2">
              Richiesta più recente per prima
            </Typography>
          </Stack>

          {query.data.resultLimitReached && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Il limite massimo di richieste esaminate è stato raggiunto.
              Restringi la ricerca o i filtri per ottenere un elenco completo.
            </Alert>
          )}

          <Stack spacing={2}>
            {query.data.results.map((request) => (
              <ManagedDropReviewCard
                key={request.id}
                product={product}
                request={request}
                stageAtRequestLabel={
                  stageLabelsById.get(request.stageAtRequest) ?? null
                }
                canReview={canReview}
                onOutcome={setOutcome}
              />
            ))}
          </Stack>

          {query.data.totalPages !== null && query.data.totalPages > 1 && (
            <Box display="flex" justifyContent="center" py={3}>
              <Pagination
                page={params.page}
                count={query.data.totalPages}
                color="primary"
                onChange={(_, nextPage) => params.setPage(nextPage)}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ManagedDropReviewPanel;
